import { Component, OnInit, OnDestroy, signal, ChangeDetectorRef, ViewChild, AfterViewInit, afterNextRender, effect, Renderer2, ElementRef, DestroyRef, inject, PLATFORM_ID } from '@angular/core';
import { FormControl, FormGroup, FormArray, Validators, ReactiveFormsModule, AbstractControl } from '@angular/forms';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';

// PrimeNG imports
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { MessageModule } from 'primeng/message';
import { SkeletonModule } from 'primeng/skeleton';
import { DynamicDialogModule } from 'primeng/dynamicdialog';
import { DatePickerModule } from 'primeng/datepicker';
import { FileUploadModule } from 'primeng/fileupload';
import { MessageService } from 'primeng/api';
import { TabsModule } from 'primeng/tabs';
import { TableModule } from 'primeng/table';
import { EditorModule } from 'primeng/editor';
import { ToastModule } from 'primeng/toast';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { StepperModule } from 'primeng/stepper';
import { FloatLabelModule } from 'primeng/floatlabel';
import { ChartModule } from 'primeng/chart';
import { ChipModule } from 'primeng/chip';
import { UIChart } from 'primeng/chart';

// Custom components
import { SuccessDialogComponent, SuccessDialogData } from '../../../shared/components/success-dialog.component';
import { TeamMemberDialogComponent, TeamMember } from '../../../components/team-member-dialog/team-member-dialog.component';
import { TeamMemberItemComponent } from '../../../components/team-member-item/team-member-item.component';
import { VestingCategoryDialogComponent, VestingCategory } from '../../../components/vesting-category-dialog/vesting-category-dialog.component';
import { UpdateDialogComponent, Update } from '../../../components/update-dialog/update-dialog.component';
import { UpdateItemComponent } from '../../../components/update-item/update-item.component';
import { OverviewNavItemComponent, OverviewNavItem } from '../../../components/overview-nav-item/overview-nav-item';
import { AllocationDialogComponent, TokenAllocation } from '../../../components/allocation-dialog/allocation-dialog.component';

@Component({
  selector: 'app-project-edit',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    CardModule,
    ButtonModule,
    InputTextModule,
    InputNumberModule,
    MessageModule,
    SkeletonModule,
    DynamicDialogModule,
    DatePickerModule,
    TabsModule,
    TableModule,
    ToastModule,
    FileUploadModule,
    EditorModule,
    InputGroupModule,
    InputGroupAddonModule,
    StepperModule,
    FloatLabelModule,
    ChartModule,
    ChipModule,
    TeamMemberItemComponent,
    UpdateItemComponent,
    OverviewNavItemComponent
  ],
  providers: [DialogService, MessageService],
  templateUrl: './project-edit.component.html',
  styleUrl: './project-edit.component.css'
})
export class ProjectEditComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('tokenChart') tokenChart!: UIChart;
  @ViewChild('chartContainer', { read: ElementRef }) chartContainer!: ElementRef;
  
  // Loading states
  loading = signal(true);
  submitting = signal(false);
  
  // Chart and tab state signals
  currentTab = signal(0);
  chartInitialized = signal(false);
  
  projectId: string | null = null;
  ref: DynamicDialogRef | undefined;
  private timeout?: number;
  private submitTimeout?: number;

  // Tab-related properties (keep for tabs functionality)
  teamMembers: TeamMember[] = [];
  vestingCategories: VestingCategory[] = [];
  updates: Update[] = [];

  // Chart data and options
  chartData: any;
  chartOptions: any;

  // Overview navigation and editors
  overviewNavItems: OverviewNavItem[] = [
    { id: '1', title: 'Our Story' },
    { id: '2', title: 'Problem' },
    { id: '3', title: 'Solution' },
    { id: '4', title: 'Technology' },
    { id: '5', title: 'Market & Audience' },
    { id: '6', title: 'Business Model' },
    { id: '7', title: 'Roadmap' }
  ];
  selectedOverviewIndex: number = 0;
  
  // Form controls for each overview section (not required)
  ourStoryControl = new FormControl('');
  problemControl = new FormControl('');
  solutionControl = new FormControl('');
  technologyControl = new FormControl('');
  marketAudienceControl = new FormControl('');
  businessModelControl = new FormControl('');
  roadmapControl = new FormControl('');
  
  // Editor configuration
  editorModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['image']
    ]
  };

  // Define individual form controls - ALL REQUIRED
  nameControl = new FormControl('', [Validators.required, Validators.minLength(3)]);
  descriptionControl = new FormControl('', [Validators.required, Validators.minLength(10)]);
  
  // Token Information
  tokenNameControl = new FormControl('', [Validators.required]);
  tokenSymbolControl = new FormControl('', [Validators.required, Validators.maxLength(10)]);
  tokenAddressControl = new FormControl('', [Validators.required]);
  totalSupplyControl = new FormControl(null, [Validators.required, Validators.min(1)]);
  
  // Swap and Claim settings
  swapOnControl = new FormControl('', [Validators.required]);
  claimOnControl = new FormControl('', [Validators.required]);
  
  // Price settings
  publicSalePriceControl = new FormControl(null, [Validators.required, Validators.min(0)]);
  privateSalePriceControl = new FormControl(null, [Validators.required, Validators.min(0)]);
  
  // Sale Information
  softCapControl = new FormControl(null, [Validators.required, Validators.min(1)]);
  hardCapControl = new FormControl(null, [Validators.required, Validators.min(1)]);
  minAllocationControl = new FormControl(null, [Validators.required, Validators.min(1)]);
  maxAllocationControl = new FormControl(null, [Validators.required, Validators.min(1)]);
  totalTokensForSaleControl = new FormControl(null, [Validators.required, Validators.min(1)]);
  poolTypesControl = new FormControl('', [Validators.required]);
  unsoldTokensControl = new FormControl('', [Validators.required]);
  
  // Investment Rounds
  privateSaleStartControl = new FormControl<Date | null>(null, [Validators.required]);
  privateSaleEndControl = new FormControl<Date | null>(null, [Validators.required]);
  publicSaleStartControl = new FormControl<Date | null>(null, [Validators.required]);
  publicSaleEndControl = new FormControl<Date | null>(null, [Validators.required]);
  tgeDateControl = new FormControl<Date | null>(null, [Validators.required]);
  tgePriceControl = new FormControl(null, [Validators.required, Validators.min(0)]);
  
  // Location and verification
  locationControl = new FormControl('', [Validators.required]);
  verifiedControl = new FormControl(false);
  micaCompliantControl = new FormControl(false);

  // Social Links (optional)
  twitterUrlControl = new FormControl('', [Validators.pattern(/^https?:\/\/.+/)]);
  githubUrlControl = new FormControl('', [Validators.pattern(/^https?:\/\/.+/)]);
  telegramUrlControl = new FormControl('', [Validators.pattern(/^https?:\/\/.+/)]);
  discordUrlControl = new FormControl('', [Validators.pattern(/^https?:\/\/.+/)]);

  // Documentation Links (optional)
  whitepaperUrlControl = new FormControl('', [Validators.pattern(/^https?:\/\/.+/)]);
  tokenomicsUrlControl = new FormControl('', [Validators.pattern(/^https?:\/\/.+/)]);

  // Project Owner
  ownerNameControl = new FormControl('', [Validators.required]);
  ownerUrlControl = new FormControl('', [Validators.required, Validators.pattern(/^https?:\/\/.+/)]);

  // Project Image
  projectImageControl = new FormControl<File | null>(null);
  projectImagePreview = signal<string | null>(null);
  projectImageError: string | null = null;

  // Form will be initialized in constructor
  projectForm!: FormGroup;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private translateService: TranslateService,
    private dialogService: DialogService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef,
    private renderer: Renderer2,
    private el: ElementRef
  ) {
    // Create form group with new structure
    this.projectForm = new FormGroup({
      name: this.nameControl,
      description: this.descriptionControl,
      
      // Token Information
      tokenName: this.tokenNameControl,
      tokenSymbol: this.tokenSymbolControl,
      tokenAddress: this.tokenAddressControl,
      totalSupply: this.totalSupplyControl,
      
      // Swap and Claim settings
      swapOn: this.swapOnControl,
      claimOn: this.claimOnControl,
      
      // Price settings
      publicSalePrice: this.publicSalePriceControl,
      privateSalePrice: this.privateSalePriceControl,
      
      // Sale Information
      softCap: this.softCapControl,
      hardCap: this.hardCapControl,
      minAllocation: this.minAllocationControl,
      maxAllocation: this.maxAllocationControl,
      totalTokensForSale: this.totalTokensForSaleControl,
      poolTypes: this.poolTypesControl,
      unsoldTokens: this.unsoldTokensControl,
      
      // Investment Rounds
      privateSaleStart: this.privateSaleStartControl,
      privateSaleEnd: this.privateSaleEndControl,
      publicSaleStart: this.publicSaleStartControl,
      publicSaleEnd: this.publicSaleEndControl,
      tgeDate: this.tgeDateControl,
      tgePrice: this.tgePriceControl,
      
      // Location and verification
      location: this.locationControl,
      verified: this.verifiedControl,
      micaCompliant: this.micaCompliantControl,

      // Project Owner
      ownerName: this.ownerNameControl,
      ownerUrl: this.ownerUrlControl,

      // Social Links
      twitterUrl: this.twitterUrlControl,
      githubUrl: this.githubUrlControl,
      telegramUrl: this.telegramUrlControl,
      discordUrl: this.discordUrlControl,

      // Documentation Links
      whitepaperUrl: this.whitepaperUrlControl,
      tokenomicsUrl: this.tokenomicsUrlControl,

      // Project Image
      projectImage: this.projectImageControl,
      
      // Overview sections for each editor
      ourStory: this.ourStoryControl,
      problem: this.problemControl,
      solution: this.solutionControl,
      technology: this.technologyControl,
      marketAudience: this.marketAudienceControl,
      businessModel: this.businessModelControl,
      roadmap: this.roadmapControl,
      
      // Keep tabs-related form arrays for later
      teamMembers: new FormArray([]),
      vestingCategories: new FormArray([]),
      updates: new FormArray([]),
      tokenAllocation: new FormArray([])
    });

    // Subscribe to token allocation changes
    this.tokenAllocationFormArray.valueChanges.subscribe(() => {
      // Always update chart data when FormArray changes, regardless of current tab
      this.forceChartReinitialization();
    });
  }

  /**
   * Fill form with test data for development and testing purposes
   */
  async fillTestData(): Promise<void> {
    // Reset the entire form first
    this.projectForm.reset();
    
    // Clear all FormArrays
    this.teamMembersFormArray.clear();
    this.vestingCategoriesFormArray.clear();
    this.updatesFormArray.clear();
    this.tokenAllocationFormArray.clear();
    
    // Basic Information
    this.nameControl.setValue('QuantLaunch');
    this.descriptionControl.setValue('Introducing a cutting-edge platform designed for secure cryptocurrency investments, enabling seamless capital allocation from a diverse pool of investors. Our comprehensive solution ensures safety and transparency, allowing you to connect with potential backers effortlessly.');
    
    // Project Image - Create a mock File object for testing
    try {
      const projectImageFile = await this.createMockImageFile('https://images.unsplash.com/photo-1639762681485-074b7f938ba0?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&h=400&q=80', 'project-image.jpg');
      this.projectImageControl.setValue(projectImageFile);
      this.projectImagePreview.set('https://images.unsplash.com/photo-1639762681485-074b7f938ba0?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&h=400&q=80');
    } catch (error) {
      console.warn('Could not create mock project image file:', error);
    }
    
    // Overview sections (optional)
    this.ourStoryControl.setValue('<h2>Our Story</h2><p>This is where we tell our story...</p>');
    this.problemControl.setValue('<h2>The Problem</h2><p>Describe the problem you are solving...</p>');
    this.solutionControl.setValue('<h2>Our Solution</h2><p>Explain your solution...</p>');
    this.technologyControl.setValue('<h2>Technology</h2><p>Detail your technology stack...</p>');
    this.marketAudienceControl.setValue('<h2>Market & Audience</h2><p>Define your target market...</p>');
    this.businessModelControl.setValue('<h2>Business Model</h2><p>Explain your business model...</p>');
    this.roadmapControl.setValue('<h2>Roadmap</h2><p>Outline your project roadmap...</p>');
    
    // Token Information
    this.tokenNameControl.setValue('Project Token');
    this.tokenSymbolControl.setValue('PTK');
    this.tokenAddressControl.setValue('0x1f2A9...e8b3b');
    this.totalSupplyControl.setValue(100000000 as any);
    
    // Swap and Claim settings
    this.swapOnControl.setValue('Ethereum');
    this.claimOnControl.setValue('Mosaic');
    
    // Price settings
    this.publicSalePriceControl.setValue(20000 as any);
    this.privateSalePriceControl.setValue(24000 as any);
    
    // Sale Information
    this.softCapControl.setValue(540000 as any);
    this.hardCapControl.setValue(1000000 as any);
    this.minAllocationControl.setValue(250 as any);
    this.maxAllocationControl.setValue(2000 as any);
    this.totalTokensForSaleControl.setValue(48000000 as any);
    this.poolTypesControl.setValue('Private Sale / Public Sale Rounds');
    this.unsoldTokensControl.setValue('Any unsold tokens will be refunded to investors proportionally');
    
    // Investment Rounds - Set dates relative to today
    const today = new Date();
    const privateSaleStart = new Date(today);
    privateSaleStart.setDate(today.getDate() + 7);
    const privateSaleEnd = new Date(privateSaleStart);
    privateSaleEnd.setDate(privateSaleStart.getDate() + 14);
    const publicSaleStart = new Date(privateSaleEnd);
    publicSaleStart.setDate(privateSaleEnd.getDate() + 3);
    const publicSaleEnd = new Date(publicSaleStart);
    publicSaleEnd.setDate(publicSaleStart.getDate() + 7);
    const tgeDate = new Date(publicSaleEnd);
    tgeDate.setDate(publicSaleEnd.getDate() + 1);
    
    this.privateSaleStartControl.setValue(privateSaleStart);
    this.privateSaleEndControl.setValue(privateSaleEnd);
    this.publicSaleStartControl.setValue(publicSaleStart);
    this.publicSaleEndControl.setValue(publicSaleEnd);
    this.tgeDateControl.setValue(tgeDate);
    this.tgePriceControl.setValue(16000 as any);
    
    // Location and verification
    this.locationControl.setValue('Hungary');
    this.verifiedControl.setValue(false);
    this.micaCompliantControl.setValue(false);
    
    // Social Links (optional)
    this.twitterUrlControl.setValue('https://twitter.com/quantlaunch');
    this.githubUrlControl.setValue('https://github.com/quantlaunch');
    this.telegramUrlControl.setValue('https://t.me/quantlaunch');
    this.discordUrlControl.setValue('https://discord.gg/quantlaunch');
    
    // Documentation Links (optional)
    this.whitepaperUrlControl.setValue('https://quantlaunch.io/whitepaper.pdf');
    this.tokenomicsUrlControl.setValue('https://quantlaunch.io/tokenomics');
    
    // Project Owner
    this.ownerNameControl.setValue('Quantum Labs');
    this.ownerUrlControl.setValue('https://quantumlabs.io');
    
    // Initialize sample data for tabs
    await this.initializeSampleTeamMembers();
    this.initializeSampleVestingCategories();
    await this.initializeSampleUpdates();
    this.initializeSampleTokenAllocation();
  }

  /**
   * Create a mock File object from a URL for testing purposes
   */
  private async createMockImageFile(url: string, filename: string): Promise<File> {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new File([blob], filename, { type: blob.type || 'image/jpeg' });
    } catch (error) {
      // Fallback: create a minimal mock file
      const blob = new Blob(['mock image data'], { type: 'image/jpeg' });
      return new File([blob], filename, { type: 'image/jpeg' });
    }
  }

  /**
   * Get error message for a form control
   */
  getErrorMessage(control: AbstractControl | null, fieldName: string): string {
    if (!control || !control.errors) {
      return '';
    }

    const errors = control.errors;
    
    if (errors['required']) {
      return this.translateService.instant('projectEdit.requiredField', { field: fieldName });
    }
    
    if (errors['minlength']) {
      return this.translateService.instant('projectEdit.minLengthError', { 
        field: fieldName, 
        min: errors['minlength'].requiredLength 
      });
    }
    
    if (errors['maxlength']) {
      return this.translateService.instant('projectEdit.maxLengthError', { 
        field: fieldName, 
        max: errors['maxlength'].requiredLength 
      });
    }
    
    if (errors['min']) {
      return this.translateService.instant('projectEdit.minValueError', { 
        field: fieldName, 
        min: errors['min'].min 
      });
    }
    
    if (errors['pattern']) {
      return this.translateService.instant('projectEdit.invalidFormat', { field: fieldName });
    }
    
    return this.translateService.instant('projectEdit.invalidField', { field: fieldName });
  }

  ngOnInit(): void {
    this.projectId = this.route.snapshot.paramMap.get('id');
    this.loadProjectData();
    
    // Initialize chart data structure early
    this.forceChartReinitialization();
  }

  ngAfterViewInit(): void {
    // Initialize chart after view init to ensure canvas is ready
    setTimeout(() => {
      if (this.chartData && this.chartData.labels && this.chartData.labels.length > 0) {
        this.forceChartReinitialization();
      }
    }, 100);
  }

  ngOnDestroy(): void {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
    if (this.submitTimeout) {
      clearTimeout(this.submitTimeout);
    }
  }

  private loadProjectData(): void {
    if (this.timeout) clearTimeout(this.timeout);
    
    this.loading.set(true);
    
    this.timeout = window.setTimeout(() => {
      // Simulate loading project data from API
      if (this.projectId && this.projectId !== '1') {
        // In edit mode, populate form with existing project data
        this.projectForm.patchValue({
          name: 'QuantLaunch',
          description: 'Introducing a cutting-edge platform designed for secure cryptocurrency investments',
          tokenName: 'Project Token',
          tokenSymbol: 'PTK',
          softCap: 540000,
          hardCap: 1000000,
          privateSaleStart: new Date('2024-06-01T10:00:00'),
          privateSaleEnd: new Date('2024-06-30T23:59:59'),
          publicSaleStart: new Date('2024-07-01T10:00:00'),
          publicSaleEnd: new Date('2024-08-31T23:59:59')
        });
      }
      
      this.loading.set(false);
    }, 2000);
  }

  // Keep only the tab-related form arrays
  // Getter for team members FormArray
  get teamMembersFormArray(): FormArray {
    return this.projectForm.get('teamMembers') as FormArray;
  }

  // Getter for vesting categories FormArray
  get vestingCategoriesFormArray(): FormArray {
    return this.projectForm.get('vestingCategories') as FormArray;
  }

  // Getter for updates FormArray
  get updatesFormArray(): FormArray {
    return this.projectForm.get('updates') as FormArray;
  }

  // Getter for token allocation FormArray
  get tokenAllocationFormArray(): FormArray {
    return this.projectForm.get('tokenAllocation') as FormArray;
  }

  onSubmit(): void {
    if (this.projectForm.valid && !this.submitting()) {
      this.submitting.set(true);
      
      // Get form data
      const formValue = this.projectForm.value;
      console.log('Raw form value:', formValue);
      
      // Create FormData for NestJS backend (handles files properly)
      const formData = this.createFormDataForBackend(formValue);
      

      /* this.submitTimeout = window.setTimeout(() => {
        // Show success toast
        const title = this.projectId === '1' 
          ? this.translateService.instant('projectEdit.success.createTitle')
          : this.translateService.instant('projectEdit.success.editTitle');
        
        const message = this.projectId === '1'
          ? this.translateService.instant('projectEdit.success.createMessage')
          : this.translateService.instant('projectEdit.success.editMessage');

        this.messageService.add({
          severity: 'success',
          summary: title,
          detail: message,
          life: 4000
        });
        
        this.submitting.set(false);
        
        // Navigate back to projects page immediately
        this.router.navigate(['/projects']);
      }, 2000);  */
    } else if (!this.projectForm.valid) {
      // Mark all fields as touched to show validation errors
      Object.keys(this.projectForm.controls).forEach(key => {
        this.projectForm.get(key)?.markAsTouched();
      });
    }
  }

  /**
   * Create FormData object for NestJS backend
   */
  private createFormDataForBackend(formValue: any): FormData {
    const formData = new FormData();
    
    // Add basic project fields
    Object.keys(formValue).forEach(key => {
      const value = formValue[key];
      
      if (key === 'projectImage' && value instanceof File) {
        formData.append('projectImage', value);
      } else if (key === 'teamMembers' && Array.isArray(value)) {
        // Handle team members with their images
        value.forEach((member, index) => {
          formData.append(`teamMembers[${index}][id]`, member.id || '');
          formData.append(`teamMembers[${index}][name]`, member.name || '');
          formData.append(`teamMembers[${index}][position]`, member.position || '');
          formData.append(`teamMembers[${index}][linkedinUrl]`, member.linkedinUrl || '');
          formData.append(`teamMembers[${index}][description]`, member.description || '');
          
          if (member.image instanceof File) {
            formData.append(`teamMembers[${index}][image]`, member.image);
          }
        });
      } else if (key === 'updates' && Array.isArray(value)) {
        // Handle updates with their cover images
        value.forEach((update, index) => {
          formData.append(`updates[${index}][id]`, update.id || '');
          formData.append(`updates[${index}][title]`, update.title || '');
          formData.append(`updates[${index}][text]`, update.text || '');
          formData.append(`updates[${index}][publishDate]`, update.publishDate ? update.publishDate.toISOString() : '');
          
          if (update.coverImage instanceof File) {
            formData.append(`updates[${index}][coverImage]`, update.coverImage);
          }
        });
      } else if (key === 'vestingCategories' && Array.isArray(value)) {
        // Handle vesting categories (no files)
        value.forEach((category, index) => {
          Object.keys(category).forEach(catKey => {
            formData.append(`vestingCategories[${index}][${catKey}]`, category[catKey] || '');
          });
        });
      } else if (key === 'tokenAllocation' && Array.isArray(value)) {
        // Handle token allocation (no files)
        value.forEach((allocation, index) => {
          Object.keys(allocation).forEach(allocKey => {
            formData.append(`tokenAllocation[${index}][${allocKey}]`, allocation[allocKey] || '');
          });
        });
      } else if (value instanceof Date) {
        formData.append(key, value.toISOString());
      } else if (value !== null && value !== undefined) {
        formData.append(key, value.toString());
      }
    });
    
    return formData;
  }

  onCancel(): void {
    this.router.navigate(['/projects']);
  }

  private showSuccessDialog(): void {
    const isNewProject = this.projectId === '1';
    const title = isNewProject 
      ? this.translateService.instant('projectEdit.success.createTitle')
      : this.translateService.instant('projectEdit.success.editTitle');
    
    const message = isNewProject
      ? this.translateService.instant('projectEdit.success.createMessage')
      : this.translateService.instant('projectEdit.success.editMessage');

    const data: SuccessDialogData = {
      title,
      message,
      buttonLabel: this.translateService.instant('common.ok')
    };

    this.ref = this.dialogService.open(SuccessDialogComponent, {
      header: '',
      width: '400px',
      modal: true,
      closable: false,
      data: data
    });

    this.ref.onClose.subscribe(() => {
      // Navigate back to projects page when dialog is closed
      this.router.navigate(['/projects']);
    });
  }

  // Error message methods
  getNameErrorMessage(): string {
    if (this.nameControl.errors?.['required']) {
      return this.translateService.instant('projectEdit.errors.nameRequired');
    }
    if (this.nameControl.errors?.['minlength']) {
      return this.translateService.instant('projectEdit.errors.nameMinLength');
    }
    return '';
  }

  getDescriptionErrorMessage(): string {
    if (this.descriptionControl.errors?.['required']) {
      return this.translateService.instant('projectEdit.errors.descriptionRequired');
    }
    if (this.descriptionControl.errors?.['minlength']) {
      return this.translateService.instant('projectEdit.errors.descriptionMinLength');
    }
    return '';
  }

  getTokenNameErrorMessage(): string {
    if (this.tokenNameControl.errors?.['required']) {
      return this.translateService.instant('projectEdit.errors.tokenNameRequired');
    }
    return '';
  }

  getTokenSymbolErrorMessage(): string {
    if (this.tokenSymbolControl.errors?.['required']) {
      return this.translateService.instant('projectEdit.errors.tokenSymbolRequired');
    }
    return '';
  }

  getSoftCapErrorMessage(): string {
    if (this.softCapControl.errors?.['required']) {
      return this.translateService.instant('projectEdit.errors.softCapRequired');
    }
    if (this.softCapControl.errors?.['min']) {
      return this.translateService.instant('projectEdit.errors.softCapMin');
    }
    return '';
  }

  getHardCapErrorMessage(): string {
    if (this.hardCapControl.errors?.['required']) {
      return this.translateService.instant('projectEdit.errors.hardCapRequired');
    }
    if (this.hardCapControl.errors?.['min']) {
      return this.translateService.instant('projectEdit.errors.hardCapMin');
    }
    return '';
  }

  private markFormGroupTouched(): void {
    this.nameControl.markAsTouched();
    this.descriptionControl.markAsTouched();
    this.tokenNameControl.markAsTouched();
    this.tokenSymbolControl.markAsTouched();
    this.softCapControl.markAsTouched();
    this.hardCapControl.markAsTouched();
    this.privateSaleStartControl.markAsTouched();
    this.privateSaleEndControl.markAsTouched();
    this.publicSaleStartControl.markAsTouched();
    this.publicSaleEndControl.markAsTouched();
  }

  // Methods needed for tabs functionality
  onOverviewNavSelected(index: number): void {
    this.selectedOverviewIndex = index;
    // Scroll to the corresponding editor
    this.scrollToOverviewSection(index);
  }

  // Method to handle scrolling to specific overview section
  scrollToOverviewSection(index: number): void {
    if (index !== null && index !== undefined) {
      const element = document.getElementById(`overview-section-${index}`);
      if (element) {
        element.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest'
        });
      }
    }
  }

  // Get the current editor control based on selected index
  getCurrentEditorControl(): FormControl {
    switch (this.selectedOverviewIndex) {
      case 0: return this.ourStoryControl;
      case 1: return this.problemControl;
      case 2: return this.solutionControl;
      case 3: return this.technologyControl;
      case 4: return this.marketAudienceControl;
      case 5: return this.businessModelControl;
      case 6: return this.roadmapControl;
      default: return this.ourStoryControl;
    }
  }

  // Team members methods
  async initializeSampleTeamMembers(): Promise<void> {
    const sampleMembers: TeamMember[] = [
      {
        id: '1',
        name: 'John Doe',
        position: 'CEO & Co-Founder',
        linkedinUrl: 'https://linkedin.com/in/johndoe',
        description: 'Experienced entrepreneur with 10+ years in blockchain technology and fintech. Previously founded two successful startups.',
        image: null,
        imagePreview: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&h=150&q=80'
      },
      {
        id: '2',
        name: 'Jane Smith',
        position: 'CTO & Co-Founder',
        linkedinUrl: 'https://linkedin.com/in/janesmith',
        description: 'Full-stack developer and blockchain architect with expertise in smart contracts and DeFi protocols. Former tech lead at major crypto exchange.',
        image: null,
        imagePreview: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&h=150&q=80'
      },
      {
        id: '3',
        name: 'Mike Johnson',
        position: 'Lead Developer',
        linkedinUrl: 'https://linkedin.com/in/mikejohnson',
        description: 'Senior software engineer specializing in distributed systems and cryptocurrency development. 8 years of experience in building scalable applications.',
        image: null,
        imagePreview: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&h=150&q=80'
      }
    ];

    this.teamMembers = sampleMembers;
    
    // Add to FormArray with File objects for images
    for (let i = 0; i < sampleMembers.length; i++) {
      const member = sampleMembers[i];
      let memberImageFile: File | null = null;
      
      try {
        memberImageFile = await this.createMockImageFile(member.imagePreview!, `team-member-${i + 1}.jpg`);
      } catch (error) {
        console.warn(`Could not create mock image file for team member ${member.name}:`, error);
      }
      
      const memberFormGroup = new FormGroup({
        id: new FormControl(member.id),
        name: new FormControl(member.name),
        position: new FormControl(member.position),
        linkedinUrl: new FormControl(member.linkedinUrl),
        description: new FormControl(member.description),
        image: new FormControl(memberImageFile)
      });
      this.teamMembersFormArray.push(memberFormGroup);
    }
  }

  openAddTeamMemberDialog(): void {
    this.ref = this.dialogService.open(TeamMemberDialogComponent, {
      header: '',
      width: '600px',
      modal: true,
      closable: true
    });

    this.ref.onClose.subscribe((teamMember: TeamMember) => {
      if (teamMember) {
        this.addTeamMember(teamMember);
      }
    });
  }

  openEditTeamMemberDialog(member: TeamMember, index: number): void {
    this.ref = this.dialogService.open(TeamMemberDialogComponent, {
      header: '',
      width: '600px',
      modal: true,
      closable: true,
      data: { teamMember: member }
    });

    this.ref.onClose.subscribe((updatedMember: TeamMember) => {
      if (updatedMember) {
        this.updateTeamMember(updatedMember, index);
      }
    });
  }

  onEditMember(event: { member: TeamMember; index: number }): void {
    this.openEditTeamMemberDialog(event.member, event.index);
  }

  addTeamMember(teamMember: TeamMember): void {
    // Generate ID for new member
    teamMember.id = Date.now().toString();
    
    // Add to local array
    this.teamMembers.push(teamMember);

    // Add to FormArray
    const memberFormGroup = new FormGroup({
      id: new FormControl(teamMember.id),
      name: new FormControl(teamMember.name),
      position: new FormControl(teamMember.position),
      linkedinUrl: new FormControl(teamMember.linkedinUrl),
      description: new FormControl(teamMember.description),
      image: new FormControl(teamMember.image)
    });
    this.teamMembersFormArray.push(memberFormGroup);

    // TODO: Here you would typically save to backend
    this.saveTeamMember(teamMember);
  }

  updateTeamMember(updatedMember: TeamMember, index: number): void {
    // Update local array
    this.teamMembers[index] = updatedMember;

    // Update FormArray
    const memberFormGroup = this.teamMembersFormArray.at(index) as FormGroup;
    memberFormGroup.patchValue({
      name: updatedMember.name,
      position: updatedMember.position,
      linkedinUrl: updatedMember.linkedinUrl,
      description: updatedMember.description,
      image: updatedMember.image
    });

    // TODO: Here you would typically save to backend
    this.saveTeamMember(updatedMember);
  }

  removeTeamMember(index: number): void {
    // Remove from local array
    this.teamMembers.splice(index, 1);

    // Remove from FormArray
    this.teamMembersFormArray.removeAt(index);

    // TODO: Here you would typically delete from backend
  }

  saveTeamMember(teamMember: TeamMember): void {
    // TODO: Implement actual save logic here
    // This method will be empty for now as requested
    console.log('Saving team member:', teamMember);
  }

  // Vesting categories methods
  initializeSampleVestingCategories(): void {
    const sampleCategories: VestingCategory[] = [
      {
        id: '1',
        category: 'Mosaic Team',
        walletAddress: '0x742d...c5436',
        cliffPeriod: '12 months',
        vestingDuration: '12 months',
        releasingFrequency: 'Monthly',
        tgeUnlock: 10,
        firstClaim: '12 months after TGE'
      },
      {
        id: '2',
        category: 'Advisory Team',
        walletAddress: '0x742d...c5436',
        cliffPeriod: '1 months',
        vestingDuration: '8 months',
        releasingFrequency: 'Monthly',
        tgeUnlock: 10,
        firstClaim: '1 month after TGE'
      },
      {
        id: '3',
        category: 'Seed Investors',
        walletAddress: '0x742d...c5436',
        cliffPeriod: '0 months',
        vestingDuration: '6 months',
        releasingFrequency: 'Monthly',
        tgeUnlock: 10,
        firstClaim: '1 month after TGE'
      },
      {
        id: '4',
        category: 'Private Investors',
        walletAddress: '0x742d...c5436',
        cliffPeriod: '8 months',
        vestingDuration: '12 months',
        releasingFrequency: 'Monthly',
        tgeUnlock: 10,
        firstClaim: '8 months after TGE'
      },
      {
        id: '5',
        category: 'Public Investors',
        walletAddress: '0x742d...c5436',
        cliffPeriod: '12 months',
        vestingDuration: '12 months',
        releasingFrequency: 'Monthly',
        tgeUnlock: 10,
        firstClaim: '12 months after TGE'
      }
    ];

    this.vestingCategories = sampleCategories;
    
    // Add to FormArray
    sampleCategories.forEach(category => {
      const categoryFormGroup = new FormGroup({
        id: new FormControl(category.id),
        category: new FormControl(category.category),
        walletAddress: new FormControl(category.walletAddress),
        cliffPeriod: new FormControl(category.cliffPeriod),
        vestingDuration: new FormControl(category.vestingDuration),
        releasingFrequency: new FormControl(category.releasingFrequency),
        tgeUnlock: new FormControl(category.tgeUnlock),
        firstClaim: new FormControl(category.firstClaim)
      });
      this.vestingCategoriesFormArray.push(categoryFormGroup);
    });
  }

  openAddVestingCategoryDialog(): void {
    this.ref = this.dialogService.open(VestingCategoryDialogComponent, {
      header: '',
      width: '600px',
      modal: true,
      closable: true
    });

    this.ref.onClose.subscribe((vestingCategory: VestingCategory) => {
      if (vestingCategory) {
        this.addVestingCategory(vestingCategory);
      }
    });
  }

  addVestingCategory(vestingCategory: VestingCategory): void {
    // Generate ID for new category
    vestingCategory.id = Date.now().toString();
    
    // Add to local array
    this.vestingCategories.push(vestingCategory);

    // Add to FormArray
    const categoryFormGroup = new FormGroup({
      id: new FormControl(vestingCategory.id),
      category: new FormControl(vestingCategory.category),
      walletAddress: new FormControl(vestingCategory.walletAddress),
      cliffPeriod: new FormControl(vestingCategory.cliffPeriod),
      vestingDuration: new FormControl(vestingCategory.vestingDuration),
      releasingFrequency: new FormControl(vestingCategory.releasingFrequency),
      tgeUnlock: new FormControl(vestingCategory.tgeUnlock),
      firstClaim: new FormControl(vestingCategory.firstClaim)
    });
    this.vestingCategoriesFormArray.push(categoryFormGroup);

    // TODO: Here you would typically save to backend
    this.saveVestingCategory(vestingCategory);
  }

  openEditVestingCategoryDialog(index: number): void {
    const vestingCategory = this.vestingCategoriesFormArray.at(index).value;
    const ref = this.dialogService.open(VestingCategoryDialogComponent, {
      header: this.translateService.instant('projectEdit.vestingCategories.editTitle'),
      width: '600px',
      data: { vestingCategory: vestingCategory }
    });

    ref.onClose.subscribe((result: VestingCategory) => {
      if (result) {
        this.updateVestingCategory(index, result);
      }
    });
  }

  updateVestingCategory(index: number, vestingCategory: VestingCategory): void {
    // Update local array
    this.vestingCategories[index] = vestingCategory;

    // Update FormArray
    this.vestingCategoriesFormArray.at(index).patchValue(vestingCategory);

    // TODO: Here you would typically save to backend
    this.saveVestingCategory(vestingCategory);
  }

  editVestingCategory(index: number): void {
    this.openEditVestingCategoryDialog(index);
  }

  removeVestingCategory(index: number): void {
    // Remove from local array
    this.vestingCategories.splice(index, 1);

    // Remove from FormArray
    this.vestingCategoriesFormArray.removeAt(index);

    // TODO: Here you would typically delete from backend
  }

  copyToClipboard(text: string): void {
    if (navigator.clipboard && text) {
      navigator.clipboard.writeText(text).then(() => {
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.copied'),
          detail: this.translateService.instant('common.copiedToClipboard'),
          life: 2000
        });
      }).catch(err => {
        console.error('Failed to copy:', err);
      });
    }
  }

  saveVestingCategory(vestingCategory: VestingCategory): void {
    // TODO: Implement actual save logic here
    // This method will be empty for now as requested
    console.log('Saving vesting category:', vestingCategory);
  }

  // Token Allocation Chart Methods
  initializeChart(): void {
    this.chartOptions = {
      responsive: false,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        }
      }
    };
    
    // Initialize chart with current data from FormArray
    this.updateChartData();
  }

  updateChartData(): void {
    const allocations = this.tokenAllocationFormArray.value;
    
    // Always create a new object to trigger change detection
    this.chartData = {
      labels: allocations.map((item: any) => item.name || 'Unnamed'),
      datasets: [{
        data: allocations.map((item: any) => item.percentage || 0),
        backgroundColor: allocations.map((item: any) => item.color || '#806EDD'),
        borderWidth: 0
      }]
    };

    // Force change detection
    this.cdr.detectChanges();
  }

  private refreshChart(): void {
    // Simply update data and trigger change detection
    this.updateChartData();
  }

  private destroyChart(): void {
    if (this.tokenChart && this.tokenChart.chart) {
      this.tokenChart.chart.destroy();
    }
    this.chartOptions = null;
    this.chartInitialized.set(false);
  }

  onChartClick(event: any): void {
    // Chart click handler - indicates chart is working
  }

  onChartTabClicked(): void {
    // Force chart reinitialization when tab is clicked
    setTimeout(() => {
      this.currentTab.set(2);
      this.forceChartReinitialization();
    }, 10);
  }

  openAddAllocationDialog(): void {
    const defaultColors = ['#806EDD', '#5C4AB8', '#FFB800', '#00D4FF', '#FF6B6B', '#4ECDC4', '#95E1D3'];
    const colorIndex = this.tokenAllocationFormArray.length % defaultColors.length;

    this.ref = this.dialogService.open(AllocationDialogComponent, {
      header: this.translateService.instant('projectEdit.tokenAllocation.addAllocation'),
      width: '450px',
      data: {
        colorIndex: colorIndex
      }
    });

    this.ref.onClose.subscribe((allocation: TokenAllocation) => {
      if (allocation) {
        const allocationGroup = new FormGroup({
          color: new FormControl(allocation.color),
          name: new FormControl(allocation.name),
          percentage: new FormControl(allocation.percentage)
        });
        this.tokenAllocationFormArray.push(allocationGroup);
      }
    });
  }

  openEditAllocationDialog(index: number): void {
    const allocation = this.tokenAllocationFormArray.at(index).value;

    this.ref = this.dialogService.open(AllocationDialogComponent, {
      header: this.translateService.instant('projectEdit.tokenAllocation.editAllocation'),
      width: '450px',
      data: {
        allocation: allocation
      }
    });

    this.ref.onClose.subscribe((updatedAllocation: TokenAllocation) => {
      if (updatedAllocation) {
        // Update the form control values individually
        const control = this.tokenAllocationFormArray.at(index) as FormGroup;
        control.get('color')?.setValue(updatedAllocation.color);
        control.get('name')?.setValue(updatedAllocation.name);
        control.get('percentage')?.setValue(updatedAllocation.percentage);
        
        // Manually trigger chart update and change detection
        this.updateChartData();
        this.cdr.detectChanges();
      }
    });
  }

  addAllocationItem(): void {
    this.openAddAllocationDialog();
  }

  removeAllocationItem(index: number): void {
    this.tokenAllocationFormArray.removeAt(index);
  }

  getTotalAllocationPercentage(): number {
    return this.tokenAllocationFormArray.value.reduce((total: number, item: any) => {
      return total + (item.percentage || 0);
    }, 0);
  }

  initializeSampleTokenAllocation(): void {
    const sampleAllocations = [
      { color: '#806EDD', name: 'Community', percentage: 24 },
      { color: '#5C4AB8', name: 'Investors', percentage: 23 },
      { color: '#FFB800', name: 'Team & Advisory', percentage: 19 },
      { color: '#00D4FF', name: 'Staking', percentage: 15 },
      { color: '#FF6B6B', name: 'Other', percentage: 19 }
    ];

    sampleAllocations.forEach(allocation => {
      const allocationGroup = new FormGroup({
        color: new FormControl(allocation.color),
        name: new FormControl(allocation.name),
        percentage: new FormControl(allocation.percentage)
      });
      this.tokenAllocationFormArray.push(allocationGroup);
    });
    
    // Force chart reinitialization after data is added
    setTimeout(() => {
      this.forceChartReinitialization();
    }, 10);
  }

  /**
   * Force complete chart reinitialization with current FormArray data
   */
  private forceChartReinitialization(): void {
    // Initialize chart options
    this.chartOptions = {
      responsive: false,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        }
      }
    };

    // Get current allocations from FormArray
    const currentAllocations = this.tokenAllocationFormArray.value;
    
    // Create chart data from current FormArray state
    this.chartData = {
      labels: currentAllocations.map((item: any) => item.name || 'Unnamed'),
      datasets: [{
        data: currentAllocations.map((item: any) => item.percentage || 0),
        backgroundColor: currentAllocations.map((item: any) => item.color || '#806EDD'),
        borderWidth: 0
      }]
    };

    // Force change detection to ensure chart rerenders
    this.cdr.detectChanges();
    this.chartInitialized.set(true);
  }

  onTabChange(event: any): void {
    // Update current tab signal
    this.currentTab.set(event.index);
    
    // When switching to the vesting categories tab (index 2), reinitialize chart
    if (event.index === 2) {
      setTimeout(() => {
        this.forceChartReinitialization();
      }, 10);
    }
  }

  // Updates methods
  async initializeSampleUpdates(): Promise<void> {
    const sampleUpdates: Update[] = [
      {
        id: '1',
        title: 'Project Development Milestone Reached',
        text: 'We are excited to announce that we have successfully completed the first phase of development. Our team has been working tirelessly to ensure that all core features are implemented according to our roadmap. This milestone includes the completion of the smart contract architecture, initial UI/UX design, and security audit preparations.',
        publishDate: new Date('2024-01-15'),
        coverImage: null,
        coverImagePreview: 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=400&q=80'
      },
      {
        id: '2', 
        title: 'Partnership Announcement with Leading DeFi Platform',
        text: 'We are thrilled to announce our strategic partnership with one of the leading DeFi platforms in the ecosystem. This collaboration will enable us to provide enhanced liquidity solutions and better user experience for our community. The partnership includes technical integration, shared marketing efforts, and joint product development initiatives.',
        publishDate: new Date('2024-02-20'),
        coverImage: null,
        coverImagePreview: 'https://images.unsplash.com/photo-1559136555-9303baea8ebd?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=400&q=80'
      },
      {
        id: '3',
        title: 'Security Audit Completed Successfully',
        text: 'Our smart contracts have undergone a comprehensive security audit by a top-tier blockchain security firm. We are pleased to report that no critical vulnerabilities were found, and all recommended improvements have been implemented. This audit provides additional confidence in the security and reliability of our platform.',
        publishDate: new Date('2024-03-10'),
        coverImage: null,
        coverImagePreview: 'https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=400&q=80'
      }
    ];

    this.updates = sampleUpdates;
    
    // Add to FormArray with File objects for cover images
    for (let i = 0; i < sampleUpdates.length; i++) {
      const update = sampleUpdates[i];
      let coverImageFile: File | null = null;
      
      try {
        coverImageFile = await this.createMockImageFile(update.coverImagePreview!, `update-${i + 1}-cover.jpg`);
      } catch (error) {
        console.warn(`Could not create mock cover image for update ${update.title}:`, error);
      }
      
      const updateFormGroup = new FormGroup({
        id: new FormControl(update.id),
        title: new FormControl(update.title),
        text: new FormControl(update.text),
        publishDate: new FormControl(update.publishDate),
        coverImage: new FormControl(coverImageFile)
      });
      this.updatesFormArray.push(updateFormGroup);
    }
  }

  openAddUpdateDialog(): void {
    this.ref = this.dialogService.open(UpdateDialogComponent, {
      header: '',
      width: '600px',
      modal: true,
      closable: true
    });

    this.ref.onClose.subscribe((update: Update) => {
      if (update) {
        this.addUpdate(update);
      }
    });
  }

  openEditUpdateDialog(update: Update, index: number): void {
    this.ref = this.dialogService.open(UpdateDialogComponent, {
      header: '',
      width: '600px',
      modal: true,
      closable: true,
      data: { update: update }
    });

    this.ref.onClose.subscribe((updatedUpdate: Update) => {
      if (updatedUpdate) {
        this.updateUpdate(updatedUpdate, index);
      }
    });
  }

  onEditUpdate(event: { update: Update; index: number }): void {
    this.openEditUpdateDialog(event.update, event.index);
  }

  addUpdate(update: Update): void {
    // Generate ID for new update
    update.id = Date.now().toString();
    
    // Add to local array
    this.updates.push(update);

    // Add to FormArray
    const updateFormGroup = new FormGroup({
      id: new FormControl(update.id),
      title: new FormControl(update.title),
      text: new FormControl(update.text),
      publishDate: new FormControl(update.publishDate),
      coverImage: new FormControl(update.coverImage)
    });
    this.updatesFormArray.push(updateFormGroup);

    // TODO: Here you would typically save to backend
    this.saveUpdate(update);
  }

  updateUpdate(updatedUpdate: Update, index: number): void {
    // Update local array
    this.updates[index] = updatedUpdate;

    // Update FormArray
    const updateFormGroup = this.updatesFormArray.at(index) as FormGroup;
    updateFormGroup.patchValue({
      title: updatedUpdate.title,
      text: updatedUpdate.text,
      publishDate: updatedUpdate.publishDate,
      coverImage: updatedUpdate.coverImage
    });

    // TODO: Here you would typically save to backend
    this.saveUpdate(updatedUpdate);
  }

  removeUpdate(index: number): void {
    // Remove from local array
    this.updates.splice(index, 1);

    // Remove from FormArray
    this.updatesFormArray.removeAt(index);

    // TODO: Here you would typically delete from backend
  }

  saveUpdate(update: Update): void {
    // TODO: Implement actual save logic here
    // This method will be empty for now as requested
    console.log('Saving update:', update);
  }

  // Image upload methods
  triggerImageUpload(): void {
    const fileInput = document.querySelector('input[type="file"][accept*="image"]') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  onImageFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    
    // Clear previous errors
    this.projectImageError = null;
    
    if (!file) {
      return;
    }

    console.log('Processing file:', file.name, file.size, file.type);

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      this.projectImageError = 'projectEdit.invalidImageType';
      console.error('Invalid file type:', file.type);
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      this.projectImageError = 'projectEdit.imageTooLarge';
      console.error('File too large:', file.size);
      return;
    }

    // Process the image
    this.processImageFile(file);
    
    // Reset input for potential re-upload
    input.value = '';
  }

  private processImageFile(file: File): void {
    const reader = new FileReader();
    
    reader.onload = (event) => {
      const result = event.target?.result as string;
      if (result) {
        console.log('Image loaded successfully, updating preview');
        this.projectImagePreview.set(result);
        this.projectImageControl.setValue(file);
      } else {
        console.error('Failed to load image');
        this.projectImageError = 'projectEdit.imageUploadError';
      }
    };

    reader.onerror = () => {
      console.error('FileReader error');
      this.projectImageError = 'projectEdit.imageUploadError';
    };

    reader.readAsDataURL(file);
  }

  onProjectImageSelect(event: any): void {
    const file = event.files[0];
    if (file) {
      // Validate file size (5MB max)
      if (file.size > 5000000) {
        this.projectImageError = 'projectEdit.imageTooLarge';
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        this.projectImageError = 'projectEdit.invalidImageType';
        return;
      }

      // Clear any previous errors
      this.projectImageError = null;

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        this.projectImagePreview.set(e.target?.result as string);
      };
      reader.readAsDataURL(file);

      // Update form control
      this.projectImageControl.setValue(file);
    }
  }

  onProjectImageUpload(event: any): void {
    // Handle successful upload if needed
    console.log('Image uploaded:', event);
  }
}